/**
 * Organization switcher (PRD §7.5).
 *
 * The active organization is a property of the *session*, not of a client-side
 * choice — the server re-checks membership on every switch (PRD §16), so this
 * screen only ever asks; it never asserts.
 */
import { useCallback, useEffect, useState } from 'react';
import type { MembershipSummary } from '@mindpeer/auth-shared';
import { Alert, Button, Card, CardHeader, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/format';

export function SelectOrganizationPage() {
  const { user, refresh } = useAuth();
  const [memberships, setMemberships] = useState<MembershipSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { memberships: items } = await api.memberships();
      setMemberships(items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your organizations.');
      setMemberships([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activate = useCallback(
    (organizationId: string) => {
      setSwitching(organizationId);
      setError(null);
      setNotice(null);
      void (async () => {
        try {
          const { organization } = await api.setActiveOrganization(organizationId);
          setNotice(`You're now working in ${organization.name}.`);
          await Promise.all([refresh().catch(() => undefined), load()]);
        } catch (caught) {
          setError(caught instanceof ApiError ? caught.message : 'Could not switch organization.');
        } finally {
          setSwitching(null);
        }
      })();
    },
    [refresh, load],
  );

  if (memberships === null) {
    return (
      <div className="flex justify-center py-12" role="status">
        <Spinner className="size-6 text-brand-600" />
        <span className="sr-only">Loading your organizations</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Organizations"
          description="Choose which organization this session works in. Modules see only the active one."
        />

        {error && <Alert tone="error" className="mb-4">{error}</Alert>}
        {notice && <Alert tone="success" className="mb-4">{notice}</Alert>}

        {memberships.length === 0 ? (
          <Alert tone="info">
            You&apos;re not a member of any organization yet. A MindPeer administrator needs to
            assign you before you can open a module.
          </Alert>
        ) : (
          <ul className="space-y-3">
            {memberships.map((membership) => (
              <MembershipRow
                key={membership.organization.id}
                membership={membership}
                isActive={membership.organization.id === user?.activeOrganization?.id}
                busy={switching === membership.organization.id}
                disabled={switching !== null}
                onActivate={activate}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function MembershipRow({
  membership,
  isActive,
  busy,
  disabled,
  onActivate,
}: {
  membership: MembershipSummary;
  isActive: boolean;
  busy: boolean;
  disabled: boolean;
  onActivate: (organizationId: string) => void;
}) {
  const { organization, roles, status } = membership;
  // A pending or suspended membership cannot be made active; the server would
  // refuse it, so don't offer the button (PRD §7.5).
  const selectable = status === 'active' && !isActive;

  return (
    <li
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg p-4 ring-1',
        isActive ? 'bg-brand-50 ring-brand-200' : 'ring-slate-200',
      )}
    >
      <div className="min-w-0">
        <p className="font-medium text-slate-900">{organization.name}</p>
        <p className="mt-0.5 text-sm text-slate-600">
          {roles.length > 0 ? roles.join(', ') : 'member'}
          {organization.type === 'global' && ' · Global organization'}
        </p>
        {status !== 'active' && (
          <p className="mt-1 text-sm text-amber-700">
            {status === 'pending'
              ? 'Awaiting approval from an administrator.'
              : `Membership ${status}.`}
          </p>
        )}
      </div>

      {isActive ? (
        <span className="rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white">
          Active
        </span>
      ) : (
        selectable && (
          <Button
            variant="secondary"
            loading={busy}
            disabled={disabled}
            onClick={() => onActivate(organization.id)}
          >
            Switch to this
          </Button>
        )
      )}
    </li>
  );
}
